<script lang="ts">
	import { getArtistSectionCount } from '../../utils/getArtistSectionCount';
	import { setArtistSectionCount } from '../../utils/setArtistSectionCount';
	import { ProjectSectionTitle } from '../ProjectSectionTitle';
	import { infiniteApiQuery } from '#lib/api/infiniteApiQuery/infiniteApiQuery.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import { gqlClient } from '#lib/gqlClient.js';
	import {
		artistSectionQueryKey,
		getArtistTalks,
	} from '#lib/websites/artists/api/getArtistSections.js';
	import {
		ARTIST_PROJECTS_LIMIT,
		TALKS_SORT,
	} from '#lib/websites/artists/pages/ArtistDetailPage/utils/artistDetailsPageLoad/artistDetailsPageLoad.js';
	import { transformInfiniteDataTalks } from '#lib/websites/artists/pages/ArtistDetailPage/utils/transformInfiniteDataTalks/index.js';
	import {
		GetArtistPageTalksDocument,
		type GetArtistPageTalksQuery,
	} from '#lib/websites/artists/queries/__generated__/getArtistPageTalks.generated.js';
	import { TalksRoutes } from '#lib/websites/talks/constants/routes.js';
	import { page } from '$app/state';
	import { Button } from '$global/components/Button';
	import { VideoCard } from '$global/components/VideoCard';
	import { getImageUrl } from '$global/utils/getImageUrl';
	import { getInfiniteData } from '$global/utils/infinite-loading/getInfiniteData';
	import { hasMoreInfiniteItems } from '$global/utils/infinite-loading/hasMoreInfiniteItems';
	import { infiniteQuery } from '$global/utils/infinite-loading/infiniteQuery';
	import { shouldRestoreInfiniteItems } from '$global/utils/infinite-loading/shouldRestoreInfiniteItems';
	import type { ArtistsPageData } from '$routes/artists/[slug]/types';

	interface Props {
		dataCy: string;
	}

	let { dataCy }: Props = $props();
	let dataCyPrefix = $derived(`${dataCy}-talks`);
	let artist = $derived(page.data.artist as ArtistsPageData['artist']);
	let variables = $derived(page.data.variables as ArtistsPageData['variables']);
	let count = $derived(artist.totalTalks?.count || 0);

	// Branches on the data the route's own loader produced rather than on the
	// flag: this component is shared, and only the loader knows which path ran.
	let useRest = $derived('restArtistSlug' in page.data);
	let slug = $derived((page.data.restArtistSlug ?? '') as string);

	// The REST page is stored in the same envelope the GraphQL page uses, and
	// cast to the document type — the document has `title` non-null and the list
	// nullable where the contract has the reverse — so the transform, getPageRows
	// and getInfiniteData below stay shared across both paths.
	let query = $derived(
		useRest
			? infiniteApiQuery({
					queryKey: artistSectionQueryKey('talks', slug, {
						limit: ARTIST_PROJECTS_LIMIT,
						offset: 0,
					}),
					queryFn: async (offset: number) =>
						({
							Artists: [
								{
									talks: (
										await getArtistTalks(undefined, slug, {
											limit: ARTIST_PROJECTS_LIMIT,
											offset,
										})
									).items,
								},
							],
						}) as GetArtistPageTalksQuery,
					limit: ARTIST_PROJECTS_LIMIT,
					maxItems: count,
				})
			: infiniteQuery({
					gqlClient,
					variables: { ...variables, sort: TALKS_SORT },
					document: GetArtistPageTalksDocument,
					limit: ARTIST_PROJECTS_LIMIT,
					maxItems: count,
				})
	);

	let talks = $derived(
		getInfiniteData({
			query,
			transform: transformInfiniteDataTalks,
		})
	);

	// Use server-side data immediately, fall back to infinite query data once loaded
	let displayItems = $derived(
		talks && talks.length > 0
			? talks
			: transformInfiniteDataTalks({
					Artists: [{ talks: artist.talks }],
				} as GetArtistPageTalksQuery)
	);

	let hasMore = $derived(
		hasMoreInfiniteItems({
			query,
			totalCount: count,
			loadedCount: displayItems?.length || 0,
			limit: ARTIST_PROJECTS_LIMIT,
			getPageRows: (queryPage) => queryPage.Artists[0]?.talks,
		})
	);

	const handleLoadMoreTalks = () => {
		setArtistSectionCount(
			'talks',
			(displayItems?.length || 0) + ARTIST_PROJECTS_LIMIT
		);
		query.fetchNextPage();
	};

	// After back/forward navigation the TanStack cache usually still holds the
	// previously loaded pages; when it has been garbage-collected, catch back
	// up to the count persisted in the history entry page by page.
	$effect(() => {
		if (
			shouldRestoreInfiniteItems({
				query,
				targetCount: getArtistSectionCount('talks'),
				loadedCount: displayItems?.length || 0,
			})
		) {
			query.fetchNextPage();
		}
	});
</script>

{#if displayItems && displayItems.length > 0}
	<div class="border-b border-gray-200"></div>
	<div>
		<ProjectSectionTitle
			title="TALKS"
			href={`${TalksRoutes.Gallery}?1=${encodeURIComponent(artist.name || '')}&1_type=3`}
			dataCy={dataCyPrefix}
			label="View all talks"
		/>
		<div
			class="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-6 lg:grid-cols-4 lg:gap-8"
		>
			{#each displayItems as talk}
				<VideoCard
					{dataCy}
					src={getImageUrl(talk?.featured_image?.filename_disk)}
					title={talk?.title ?? ''}
					duration={talk?.video_duration ?? ''}
					href={`${GlobalRoutes.TalksHome}/${talk?.slug ?? ''}`}
					description={talk?.short_description ?? ''}
					variant="light"
				/>
			{/each}
		</div>
		{#if hasMore}
			<Button
				dataCy={`${dataCyPrefix}-load-more`}
				variant="secondary"
				size="md"
				class="mx-auto mb-8 w-full md:w-fit"
				loading={query.isFetchingNextPage}
				onclick={handleLoadMoreTalks}>Show more</Button
			>
		{/if}
	</div>
{/if}
